import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openCalendlyPopup } from "@/lib/calendly";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Brain, Check, Lock, ShieldQuestion, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { brainTypeMeta } from "@/lib/brainTypeGraphics";
import { brainTypeReport } from "@/lib/brainTypeReports";
import BrainTypeReport from "@/components/BrainTypeReport";

const TYPES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const ROLE_LABEL_KEY = {
  super_admin: "role.super_admin",
  institute_admin: "role.institute_admin",
  coach: "role.coach",
  user: "role.user",
};

export default function BrainType() {
  const { user, setUser } = useAuth();
  const { t } = useT();
  const [selected, setSelected] = useState(user?.brain_type ?? null);
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  const readOnly = user?.role === "user";
  const dirty = (user?.brain_type ?? null) !== selected;
  const meta = brainTypeMeta(user?.brain_type);
  const report = brainTypeReport(user?.brain_type);

  // Preliminary brain type panel: only shown to end users whose definitive
  // brain type has not been set yet by a coach.
  const showPreliminaryPanel = readOnly && user?.brain_type == null;
  const preliminary = user?.preliminary_brain_type ?? null;
  const preliminaryMeta = brainTypeMeta(preliminary);

  // Same 2-paragraph preview shown on the onboarding BT-test result screen —
  // gives the user a glimpse of their preliminary type while keeping the
  // unlock value of the coach double-check intact.
  const preliminaryPreviewParagraphs = (() => {
    if (!preliminary) return [];
    const r = brainTypeReport(preliminary);
    if (!r || !r.length) return [];
    const firstSection = r[0];
    if (!firstSection || !firstSection.body) return [];
    return firstSection.body.split(/\n\s*\n/).slice(0, 2);
  })();

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me/brain-type", { brain_type: selected });
      setUser(data);
      toast.success("Brain type saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const requestConsultation = async () => {
    setRequesting(true);
    try {
      await api.post("/qol/me/request-coaching", {
        reason: "brain_type_consultation",
        note: preliminary
          ? `Client requests a consultation to confirm brain type. Quick-test result: #${preliminary}.`
          : "Client requests a consultation to determine their brain type.",
      });
      setRequested(true);
      toast.success("Your coach has been notified. They'll reach out to schedule.");
      // Open Calendly so the client can book the double-check meeting directly.
      openCalendlyPopup(user?.coach_calendly_embed_code || user?.coach_calendly_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send request");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="px-8 lg:px-12 py-12 max-w-4xl" data-testid="brain-type-page">
      <PageHeader
        overline={`${t("overline.profile")} · ${t(ROLE_LABEL_KEY[user?.role] || "role.user")}`}
        title={t("brain_type.title")}
        description={readOnly ? t("brain_type.description_user") : t("brain_type.description_other")}
      />

      <div
        className="bg-white border border-[#E5E2DC] rounded-2xl p-8 mb-8"
        data-testid="brain-type-current"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
              <Brain strokeWidth={1.5} className="text-[#B37A5F]" size={24} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
                {readOnly ? t("brain_type.assigned") : t("brain_type.current")}
              </p>
              <p
                className="font-heading text-3xl text-[#1A1D1A] font-semibold tracking-tight mt-1"
                data-testid="brain-type-current-value"
              >
                {user?.brain_type ?? t("brain_type.not_set")}
                {meta && (
                  <span className="ml-3 text-xl font-light text-[#5C635A]" data-testid="brain-type-current-name">
                    · {meta.name}
                  </span>
                )}
              </p>
            </div>
          </div>
          {readOnly && (
            <div className="hidden sm:flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
              <Lock strokeWidth={1.5} size={14} />
              {t("brain_type.coach_assigned")}
            </div>
          )}
        </div>

        {readOnly ? (
          <div
            className="bg-[#F2F0EA] border border-[#E5E2DC] rounded-xl p-6"
            data-testid="brain-type-readonly-note"
          >
            <p className="text-sm text-[#5C635A] leading-relaxed">
              {t("brain_type.readonly_note")}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A] mb-4">
              {t("brain_type.select")}
            </p>
            <div
              className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3"
              data-testid="brain-type-grid"
            >
              {TYPES.map((n) => {
                const active = selected === n;
                return (
                  <button
                    key={n}
                    onClick={() => setSelected(n)}
                    data-testid={`brain-type-option-${n}`}
                    className={`aspect-square rounded-xl border font-heading text-2xl font-semibold tracking-tight transition-colors ${
                      active
                        ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
                        : "bg-white text-[#1A1D1A] border-[#E5E2DC] hover:bg-[#F2F0EA] hover:border-[#4A5D4E]/40"
                    }`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => setSelected(null)}
                data-testid="brain-type-clear-button"
                className="text-xs uppercase tracking-[0.2em] text-[#8A9188] hover:text-[#1A1D1A] font-bold"
              >
                {t("brain_type.clear")}
              </button>
              <Button
                onClick={save}
                disabled={!dirty || saving}
                data-testid="brain-type-save-button"
                className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
              >
                <Check strokeWidth={1.5} size={18} className="mr-2" />
                {saving ? t("common.saving") : t("brain_type.save")}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Preliminary brain type panel — end users only, when no definitive type set */}
      {showPreliminaryPanel && (
        <div
          className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-10 mb-8"
          data-testid="preliminary-brain-type-panel"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
              <ShieldQuestion strokeWidth={1.5} className="text-[#B37A5F]" size={24} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
                {preliminary ? t("brain_type.preliminary_overline") : t("brain_type.notset_overline")}
              </p>
              <h3 className="font-heading text-2xl sm:text-3xl text-[#1A1D1A] tracking-tight font-semibold mt-1">
                {preliminary ? (
                  <>
                    {t("brain_type.preliminary_voorlopig")}{" "}
                    <span className="font-semibold" data-testid="preliminary-brain-type-value">
                      #{preliminary}
                    </span>
                    {preliminaryMeta && (
                      <span className="ml-3 text-lg font-light text-[#5C635A]">
                        · {preliminaryMeta.name}
                      </span>
                    )}
                  </>
                ) : (
                  <>{t("brain_type.notset_heading")}</>
                )}
              </h3>
            </div>
          </div>

          {preliminary ? (
            <p className="text-sm sm:text-base text-[#5C635A] leading-relaxed max-w-2xl mb-6">
              {t("brain_type.preliminary_description")}
            </p>
          ) : (
            <p className="text-sm sm:text-base text-[#5C635A] leading-relaxed max-w-2xl mb-6">
              {t("brain_type.notset_description")}
            </p>
          )}

          {preliminary && preliminaryPreviewParagraphs.length > 0 && (
            <div
              className="bg-[#F9F8F6] border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 mb-6 max-w-3xl"
              data-testid="preliminary-brain-type-preview"
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B37A5F] mb-4">
                {t("bt_test.preview.overline")}
              </p>
              <div className="space-y-4 text-[#2A2D2A] leading-relaxed">
                {preliminaryPreviewParagraphs.map((p, i) => (
                  <p key={i} data-testid={`preliminary-brain-type-preview-paragraph-${i}`}>
                    {p}
                  </p>
                ))}
              </div>
              <p
                className="mt-6 pt-5 border-t border-[#E5E2DC] text-sm text-[#5C635A] leading-relaxed italic"
                data-testid="preliminary-brain-type-preview-disclaimer"
              >
                {t("bt_test.preview.disclaimer")}
              </p>
            </div>
          )}

          <Button
            onClick={requestConsultation}
            disabled={requesting || requested}
            data-testid="request-consultation-button"
            className="h-12 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white px-6"
          >
            {requested ? (
              <>
                <CheckCircle2 strokeWidth={1.5} size={18} className="mr-2" />
                {t("brain_type.coach_notified_pending")}
              </>
            ) : (
              <>
                <Sparkles strokeWidth={1.5} size={18} className="mr-2" />
                {requesting ? t("brain_type.sending_request") : t("brain_type.request_consultation")}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Brain type graphic */}
      {user?.brain_type != null && (
        <div
          className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-10"
          data-testid="brain-type-graphic-card"
        >
          {meta ? (
            <div className="flex flex-col items-center gap-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
                Type {user.brain_type}
              </p>
              <h3
                className="font-heading text-3xl sm:text-4xl text-[#1A1D1A] tracking-tight font-semibold text-center"
                data-testid="brain-type-graphic-name"
              >
                {meta.name}
              </h3>
              <img
                src={meta.image}
                alt={`${meta.name} — Brain Type ${user.brain_type}`}
                data-testid="brain-type-graphic-image"
                className="w-full max-w-2xl h-auto"
                loading="lazy"
              />
            </div>
          ) : (
            <div
              className="bg-[#F2F0EA] border border-[#E5E2DC] rounded-xl p-8 text-center"
              data-testid="brain-type-graphic-pending"
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188] mb-2">
                Type {user.brain_type}
              </p>
              <p className="text-[#5C635A] leading-relaxed">
                Graphic for this brain type is coming soon.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Brain type report (per-type narrative) */}
      <BrainTypeReport report={report} />
    </div>
  );
}
